import { DefaultAgencyManager } from 'core';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { getSalesChannelName } from 'utils/I18nUtils';

const agencyManager = new DefaultAgencyManager();
const adRequestSourceManager = new DefaultAdRequestSourceManager();

export const useGetPmpOptions = () => {
  const [channelSpaceOptionsMap, setChannelSpaceOptionsMap] = useState<{
    [channelId: number]: SelectOptions[];
  }>({});
  const [channelAgencyOptionsMap, setChannelAgencyOptionsMap] = useState<{
    [channelId: number]: SelectOptions[];
  }>({});

  const { callAPIsBySequence, callAPIs } = useCallAPI();
  const [optionInited, setOptionInited] = useState(false);

  const fetchOptionsOfChannel = useCallback(
    async (channelId: number) => {
      const channelGroupSetting = ChannelGroupSetting.fromId(channelId);
      return callAPIs(
        [
          () =>
            agencyManager.getAgenciesOptions(
              `channel.${channelGroupSetting.config.addonName}`
            ),
          () =>
            // PMP 建單機台下拉屬「新選版位」情境，deliverableOnly=true 只列出可投遞
            // （ON 且未在 RMax 刪除）的版位，避免選到停止服務或已刪除的機台。
            adRequestSourceManager.getSpacesWithConditions(
              true,
              {},
              'Outdoor',
              channelId,
              undefined,
              false,
              true
            )
        ],
        (agencyOptions: SelectOptions[], spaceOptions: SelectOptions[]) => {
          setChannelSpaceOptionsMap((prev) => ({
            ...prev,
            [channelId]: spaceOptions
          }));
          setChannelAgencyOptionsMap((prev) => ({
            ...prev,
            [channelId]: agencyOptions
          }));
        }
      );
    },
    [callAPIs]
  );

  const pmpChannelGroupSettings = useMemo(() => {
    // ECOPD-5566：停用渠道的 PMP 選項強制關閉
    return ChannelGroupSetting.activeValues().filter(
      (setting) => setting.config.addonFeatures.campaign.supportPMP || false
    );
  }, []);

  const channelOptions = useMemo(() => {
    return pmpChannelGroupSettings.map((setting) => ({
      label: getSalesChannelName(setting.config.id, setting.config.name),
      value: setting.config.id
    }));
  }, [pmpChannelGroupSettings]);

  useEffect(() => {
    callAPIsBySequence(
      [
        ...pmpChannelGroupSettings.map(
          (setting) => () => fetchOptionsOfChannel(setting.config.id)
        )
      ],
      () => {
        setOptionInited(true);
      }
    );
  }, [pmpChannelGroupSettings, callAPIsBySequence, fetchOptionsOfChannel]);

  return {
    optionInited,
    channelOptions,
    channelSpaceOptionsMap,
    channelAgencyOptionsMap
  };
};
